import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Icons } from "../../assest/icons";
import Header from './header';
import LoaderSpinner from '../loaderSpinner';
import { logger } from '../../utils/logger';
import SideBarMenu from './sideBarMenu';
import { getAllUsers, updateUser } from '../../services/firestore';
import moment from 'moment';

// Safely parse dates from various formats (ISO string, Firestore Timestamp, Date object)
const parseDate = (val) => {
  if (!val) return null;
  // Firestore Timestamp object (has seconds or _seconds)
  if (typeof val === 'object' && (val.seconds != null || val._seconds != null)) {
    return new Date((val.seconds || val._seconds) * 1000);
  }
  // Firestore Timestamp with toDate()
  if (typeof val?.toDate === 'function') {
    return val.toDate();
  }
  // String or Date
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

function AllPatients() {
  let navigate = useNavigate();

  const routeChange = (id) => {
    localStorage.setItem('adminCurrentPatientId', id);
    navigate(`/admin/patientprofile?pid=${encodeURIComponent(id)}`, {
      state: { id: id }
    });
  };

  const [ShowLoader, setShowLoader] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [CheckString, setCheckString] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('all');
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setShowLoader(true);
    try {
      const users = await getAllUsers();
      // Sort by created date descending (newest first)
      users.sort((a, b) => {
        const aDate = parseDate(a.created_on || a.created_at);
        const bDate = parseDate(b.created_on || b.created_at);
        return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
      });
      setAllUsers(users);
    } catch (error) {
      logger.error('Error fetching all patients:', error);
    }
    setShowLoader(false);
  };

  const handleToggleStatus = async (e, patient) => {
    e.stopPropagation(); // prevent row click navigation
    const patientId = patient.user_id || patient.id || '';
    const currentStatus = patient.accountStatus || 'active';
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingId(patientId);
    try {
      await updateUser(patientId, { accountStatus: newStatus });
      setAllUsers(prev => prev.map(u =>
        (u.user_id || u.id) === patientId ? { ...u, accountStatus: newStatus } : u
      ));
      toast.success(
        `${patient.fullName} is now ${newStatus}`,
        { position: toast.POSITION.TOP_RIGHT }
      );
    } catch (error) {
      logger.error('Toggle status error:', error);
      toast.error('Failed to update status', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setTogglingId(null);
    }
  };

  // Filtered users based on search + status
  const filteredUsers = allUsers.filter((user) => {
    // Status filter
    if (filterStatus !== 'all') {
      const status = user.accountStatus || 'active';
      if (status !== filterStatus) return false;
    }
    // Search filter
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      return (
        user.fullName?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        user.phoneNumber?.includes(searchText)
      );
    }
    return true;
  });

  const activeCount = allUsers.filter(u => (u.accountStatus || 'active') === 'active').length;
  const inactiveCount = allUsers.filter(u => u.accountStatus === 'inactive').length;

  const totalRecords = filteredUsers.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage) || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + recordsPerPage);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCheckString(e.target.value.length > 0);
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearchText('');
    setCheckString(false);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getFirstAndLastChars = (str) => {
    if (!str) return '';
    const words = str.split(' ');
    if (words.length === 0) return '';
    const firstChar = words[0].charAt(0).toUpperCase();
    const lastChar = words[words.length - 1].charAt(0).toUpperCase();
    return `${firstChar}${lastChar}`;
  };

  const renderTableBody = () => {
    if (!paginatedUsers || paginatedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="8" style={{ textAlign: 'center' }}>No data found</td>
        </tr>
      );
    }

    return paginatedUsers.map((patient, index) => {
      const patientId = patient.user_id || patient.id || '';
      const status = patient.accountStatus || 'active';
      return (
        <tr
          key={index}
          onClick={() => routeChange(patientId)}
          style={{ cursor: 'pointer' }}
        >
          <td>
            <span className="d-flex align-items-center patientFullnameTxt">
              <b className="userImgIconCircleDiv">
                {getFirstAndLastChars(patient.fullName)}
              </b>
              <span className="fullnameTxt">{patient.fullName}</span>
            </span>
          </td>
          <td>{patient.email}</td>
          <td>
            {patient.phoneNumber
              ? patient.phoneNumber.slice(0, -2).replace(/\d/g, 'X') + patient.phoneNumber.slice(-2)
              : '-'}
          </td>
          <td style={{ textTransform: 'capitalize' }}>{patient.gender || '-'}</td>
          <td>{(() => { const d = parseDate(patient.dob); if (d) return moment(d).format('MMM DD, YYYY'); if (patient.dob && typeof patient.dob === 'string') { const m = moment(patient.dob, ['YYYY-MM-DD', 'DD-MM-YYYY']); return m.isValid() ? m.format('MMM DD, YYYY') : '-'; } return '-'; })()}</td>
          <td>{(() => { const d = parseDate(patient.created_on || patient.created_at); return d ? moment(d).format('MMM DD, YYYY') : '-'; })()}</td>
          <td>
            <span className={`portal-status-badge portal-status-${status}`}>
              {status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td>
            <div className="switch-container" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                id={`toggle-${patientId}`}
                className="switch"
                checked={status === 'active'}
                onChange={(e) => handleToggleStatus(e, patient)}
                disabled={togglingId === patientId}
              />
              <label className="switch-slider" htmlFor={`toggle-${patientId}`}>
                <span className="switch-knob"></span>
              </label>
            </div>
          </td>
        </tr>
      );
    });
  };

  const renderPagination = () => {
    if (!paginatedUsers || paginatedUsers.length === 0) return null;

    let pages = [];
    const startPage = (Math.floor((currentPage - 1) / 3) * 3) + 1;
    const endPage = Math.min(startPage + 2, totalPages);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`page-button ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          Previous
        </button>
        {pages}
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>
    );
  };

  return (
    <div>
      {ShowLoader && <LoaderSpinner />}
      <div className='container-fluid p-0 adminDashboardMain'>
        <SideBarMenu />

        <div className='container-fluid p-0 contentBody'>
          <Header />
          <div className="innerBodyBox bgInnerDiv patientDataListMain">
            <div className="contentInnerBody">
              <div className="titleDiv">
                <h2>All Patients</h2>

                <div className="">
                  <div className="leftDiv d-inline-block searchAdminUser">
                    <div className="inputDivBox">
                      <img src={Icons[0].admin_searchIcon} alt="search-icon" />
                      <input
                        type="text"
                        placeholder="Search patient name, email, phone"
                        value={searchText}
                        onChange={handleSearchChange}
                      />
                      <img
                        className={"reset-button " + (CheckString ? 'b' : 'd-none')}
                        src={Icons[0].admin_deleteIcon}
                        alt="filter-icon"
                        onClick={handleResetSearch}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                  <ul className="titleRightSideDiv">
                    <li>
                      <div className="table-options">
                        <label htmlFor="filterStatusPatient">Status:</label>
                        <select
                          id="filterStatusPatient"
                          value={filterStatus}
                          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                          style={{ border: 0, background: 'transparent', cursor: 'pointer' }}
                        >
                          <option value="all">All ({allUsers.length})</option>
                          <option value="active">Active ({activeCount})</option>
                          <option value="inactive">Inactive ({inactiveCount})</option>
                        </select>
                      </div>
                    </li>
                    <li>Total: {totalRecords}</li>
                    <li>
                      <div className="table-options">
                        <label htmlFor="recordsPerPage">Showing</label>
                        <select id="recordsPerPage"
                          value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </li>
                  </ul>
                </div>

              </div>
              <div className="contentMainBox">
                <div className="table-container">
                  <table id="patientTable">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Gender</th>
                        <th>DOB</th>
                        <th>Created Date</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderTableBody()}
                    </tbody>
                  </table>
                </div>
                <div className="paginationMain">
                  {renderPagination()}
                  <span className={"totalPageDiv " + (!totalPages ? 'd-none' : '')}>Total Pages: {totalPages}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllPatients;
